// The theme generator: a whole theme from a few decisions (light or dark, a base hue and tint, a colour-wheel scheme, how
// vivid), every ink solved to read on what it sits on. It draws the tokens with no formula, and makes the ones with a
// formula (the page and the neutral steps, the softer inks, the lines, the corner steps) from those, as plain values
// (fill.ts). Randomising is the generator with fresh decisions, at three levels: the whole theme, one group, one token.
// Pinned groups and tokens keep their values; a token that reads one that changed is made again (a token with no formula
// solved again, keeping its own hue and chroma), so the theme still reads.
import { BASE_TOKENS, TOKEN_GROUPS, deriveCss, tokenSpec } from './tokens';
import { FORMULA_TOKENS, madeByFormula, readersOf } from './fill';
import { contrast, formatOklch, oklchToSrgb, readColour, solveLightness, srgbToOklch, themeValues, tokenColour, type Oklch, type Rgb } from './colour';
import { FILL_SLOTS, contrastShortfalls, type ContrastShortfall } from './contrastRules';
import { MONO_FACES, UI_FACES } from './fonts';
import { PATTERN_NAMES } from './texture';

export type Random = () => number;

/** A repeatable random source from a seed (mulberry32), so a theme can be made again and tested. */
export function seededRandom(seed: number): Random {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** How the brand and accent sit against the base hue on the colour wheel. */
export const SCHEMES = [
  { id: 'complementary', label: 'complementary', turns: [180, 180] },
  { id: 'split', label: 'split complementary', turns: [150, 210] },
  { id: 'triadic', label: 'triadic', turns: [120, 240] },
  { id: 'analogous', label: 'analogous', turns: [30, -30] },
  { id: 'tetradic', label: 'tetradic', turns: [90, 180] },
] as const;
export type Scheme = (typeof SCHEMES)[number];

/** The decisions a theme is made from. */
export interface ThemeSeeds {
  mode: 'light' | 'dark';
  /** The base hue of the page, its text and its deep fills. */
  hue: number;
  /** How tinted the neutrals are (OKLCH chroma). */
  tint: number;
  scheme: Scheme;
  /** How vivid the brand and status colours are (OKLCH chroma). */
  vivid: number;
}

/** Whether the theme's groups or single tokens are pinned, so randomising leaves them as they are. */
export interface Pins {
  groups: ReadonlySet<string>;
  tokens: ReadonlySet<string>;
}

export const NO_PINS: Pins = { groups: new Set(), tokens: new Set() };

/** What to randomise: the whole theme, one group of tokens, or one token. */
export type Scope = { level: 'theme' } | { level: 'group'; id: string } | { level: 'token'; name: string };

const GROUP_OF = new Map(TOKEN_GROUPS.flatMap(group => group.tokens.map(token => [token.name, group.id] as const)));
export const groupOf = (name: string) => GROUP_OF.get(name) ?? '';

// How a token is made: fresh from the decisions, or solved again from its current value when what it depends on changed
interface Rule {
  /** The tokens it reads, so it is solved again when one changes. */
  deps: string[];
  make: (ctx: Context, fresh: boolean) => string;
}

interface Context {
  seeds: ThemeSeeds;
  random: Random;
  tokens: Record<string, string>;
}

const between = (ctx: Context, lo: number, hi: number) => lo + (hi - lo) * ctx.random();
const pick = <T>(ctx: Context, list: readonly T[]): T => list[Math.floor(ctx.random() * list.length)];
const light = (ctx: Context) => ctx.seeds.mode === 'light';
const wrap = (h: number) => ((h % 360) + 360) % 360;

/** A token's colour as OKLCH, one the theme sets or one derived from those; black where it cannot be read. */
function colourOf(ctx: Context, name: string): Oklch {
  const colour = tokenColour(name, themeValues(ctx.tokens, ctx.seeds.mode));
  return colour ? srgbToOklch(colour.rgb) : { l: 0, c: 0, h: 0 };
}
const rgbOf = (ctx: Context, name: string): Rgb => oklchToSrgb(colourOf(ctx, name));

/** A colour that reads at `minimum` against a token, at this hue and chroma. */
const solved = (ctx: Context, hue: number, chroma: number, against: string, minimum: number) =>
  formatOklch({ l: solveLightness(hue, chroma, rgbOf(ctx, against), minimum), c: chroma, h: hue });

/** A colour from the decisions, its lightness pushed until it reads at `minimum` against `against` where it does not. */
function readable(ctx: Context, colour: Oklch, against: string, minimum: number): string {
  if (contrast(oklchToSrgb(colour), rgbOf(ctx, against)) >= minimum) return formatOklch(colour);
  return solved(ctx, colour.h, colour.c, against, minimum);
}

/** A neutral offset from the page (a step below the surface) by `light` in a light theme and `dark` in a dark one. */
function fromPage(ctx: Context, lightOffset: number, darkOffset: number, tintScale = 1): string {
  const page = colourOf(ctx, 'lureo-page');
  return formatOklch({ l: page.l + (light(ctx) ? lightOffset : darkOffset), c: page.c * tintScale, h: page.h });
}

/** A colour rule that keeps its hue and chroma when solved again, and finds them fresh from `pickHue` and `pickChroma`. */
function inked(against: string, minimum: number, pickHue: (ctx: Context) => number, pickChroma: (ctx: Context) => number, self?: string): Rule['make'] {
  return (ctx, fresh) => {
    const current = self ? colourOf(ctx, self) : null;
    const hue = fresh || !current ? pickHue(ctx) : current.h;
    const chroma = fresh || !current ? pickChroma(ctx) : current.c;
    return solved(ctx, hue, chroma, against, minimum);
  };
}

/** A fill at a lightness band that keeps `text` readable on it. */
function filled(name: string, text: string, minimum: number, pickColour: (ctx: Context) => Oklch): Rule['make'] {
  return (ctx, fresh) => {
    const colour = fresh ? pickColour(ctx) : colourOf(ctx, name);
    return readable(ctx, colour, text, minimum);
  };
}

/** The large corner, the roundness × 4/3, in the roundness's own unit. */
function largeCorner(ctx: Context): string {
  const m = (ctx.tokens['lureo-radius'] ?? '').trim().match(/^(-?\d*\.?\d+)([a-z%]*)$/);
  return m ? `${Number(((Number(m[1]) * 4) / 3).toFixed(4))}${m[2]}` : ctx.tokens['lureo-radius'];
}

/** Whether the call to action is the accent rather than the brand: whichever its colour is nearer. */
function ctaIsAccent(ctx: Context): boolean {
  const near = (name: string) => {
    const [a, b] = [colourOf(ctx, 'lureo-cta'), colourOf(ctx, name)];
    return Math.hypot(a.l - b.l, a.c * Math.cos((a.h * Math.PI) / 180) - b.c * Math.cos((b.h * Math.PI) / 180), a.c * Math.sin((a.h * Math.PI) / 180) - b.c * Math.sin((b.h * Math.PI) / 180));
  };
  return near('lureo-accent') <= near('lureo-brand');
}

const brandHue = (ctx: Context) => wrap(ctx.seeds.hue + ctx.seeds.scheme.turns[0] * (ctx.random() < 0.5 ? 1 : -1) + between(ctx, -8, 8));

// The tokens with no formula the generator draws; what a token reads is drawn before it
const RULES: Record<string, Rule> = {
  'lureo-surface': { deps: [], make: (ctx, fresh) => (fresh ? formatOklch({ l: light(ctx) ? between(ctx, 0.975, 0.995) : between(ctx, 0.2, 0.27), c: ctx.seeds.tint * 0.6, h: ctx.seeds.hue }) : ctx.tokens['lureo-surface']) },
  'lureo-ink-strong': { deps: ['lureo-surface'], make: inked('lureo-page', 13, ctx => ctx.seeds.hue, ctx => ctx.seeds.tint * 2, 'lureo-ink-strong') },
  'lureo-on-emphasis': { deps: [], make: (ctx, fresh) => (fresh ? formatOklch({ l: 0.985, c: ctx.seeds.tint * 0.5, h: ctx.seeds.hue }) : ctx.tokens['lureo-on-emphasis']) },
  'lureo-brand': { deps: ['lureo-on-emphasis'], make: filled('lureo-brand', 'lureo-on-emphasis', 4.6, ctx => ({ l: between(ctx, 0.38, 0.52), c: ctx.seeds.vivid, h: brandHue(ctx) })) },
  'lureo-brand-ink': { deps: ['lureo-surface', 'lureo-brand'], make: ctx => solved(ctx, colourOf(ctx, 'lureo-brand').h, Math.max(colourOf(ctx, 'lureo-brand').c, 0.06), 'lureo-page', 4.6) },
  // A generated theme's links are its brand ink, and the text on its brand fill the text on its deep fills
  'lureo-link': { deps: ['lureo-brand-ink'], make: ctx => ctx.tokens['lureo-brand-ink'] },
  'lureo-on-brand': { deps: ['lureo-on-emphasis'], make: ctx => ctx.tokens['lureo-on-emphasis'] },
  'lureo-secondary': { deps: ['lureo-on-emphasis', 'lureo-brand'], make: filled('lureo-secondary', 'lureo-on-emphasis', 4.6, ctx => ({ l: between(ctx, 0.38, 0.5), c: ctx.seeds.vivid * 0.9, h: wrap(colourOf(ctx, 'lureo-brand').h + between(ctx, 25, 60)) })) },
  'lureo-accent': { deps: ['lureo-emphasis'], make: filled('lureo-accent', 'lureo-on-emphasis', 1, ctx => ({ l: between(ctx, 0.84, 0.91), c: ctx.seeds.vivid + 0.04, h: wrap(ctx.seeds.hue + ctx.seeds.scheme.turns[1]) })) },
  'lureo-emphasis': { deps: ['lureo-on-emphasis'], make: filled('lureo-emphasis', 'lureo-on-emphasis', 9, ctx => ({ l: between(ctx, 0.24, 0.31), c: between(ctx, 0.04, 0.09), h: ctx.seeds.hue })) },
  'lureo-emphasis-strong': { deps: ['lureo-on-emphasis', 'lureo-emphasis'], make: filled('lureo-emphasis-strong', 'lureo-on-emphasis', 10, ctx => ({ l: between(ctx, 0.2, 0.26), c: between(ctx, 0.05, 0.1), h: wrap(colourOf(ctx, 'lureo-emphasis').h + between(ctx, 30, 60)) })) },
  'lureo-success': { deps: [], make: (ctx, fresh) => (fresh ? formatOklch({ l: between(ctx, 0.56, 0.66), c: between(ctx, 0.12, 0.17), h: between(ctx, 138, 152) }) : ctx.tokens['lureo-success']) },
  'lureo-success-ink': { deps: ['lureo-surface', 'lureo-success'], make: ctx => solved(ctx, colourOf(ctx, 'lureo-success').h, 0.13, 'lureo-page', 4.6) },
  'lureo-warning': { deps: [], make: (ctx, fresh) => (fresh ? formatOklch({ l: between(ctx, 0.74, 0.81), c: between(ctx, 0.13, 0.17), h: between(ctx, 68, 82) }) : ctx.tokens['lureo-warning']) },
  'lureo-warning-ink': { deps: ['lureo-surface', 'lureo-warning'], make: ctx => solved(ctx, colourOf(ctx, 'lureo-warning').h, 0.12, 'lureo-page', 3.2) },
  'lureo-danger': { deps: ['lureo-on-emphasis'], make: filled('lureo-danger', 'lureo-on-emphasis', 3.2, ctx => ({ l: between(ctx, 0.55, 0.62), c: between(ctx, 0.18, 0.22), h: between(ctx, 22, 32) })) },
  'lureo-danger-ink': { deps: ['lureo-surface', 'lureo-danger'], make: ctx => solved(ctx, colourOf(ctx, 'lureo-danger').h, 0.17, 'lureo-page', 4.6) },
  // The call to action: bright (the accent, the deep emphasis as text on it) or deep (the brand, its own text on it)
  'lureo-cta': {
    deps: ['lureo-brand', 'lureo-accent'],
    make: (ctx, fresh) => ctx.tokens[(fresh ? ctx.random() < 0.6 : ctaIsAccent(ctx)) ? 'lureo-accent' : 'lureo-brand'],
  },
  'lureo-on-cta': { deps: ['lureo-cta', 'lureo-on-brand', 'lureo-emphasis'], make: ctx => ctx.tokens[ctaIsAccent(ctx) ? 'lureo-emphasis' : 'lureo-on-brand'] },
  'lureo-sheen': { deps: [], make: (ctx, fresh) => (fresh ? (ctx.random() < 0.4 ? '1' : '0') : ctx.tokens['lureo-sheen']) },
  'lureo-glass': { deps: [], make: (ctx, fresh) => (fresh ? String(pick(ctx, [0, 0, 0.5, 1])) : ctx.tokens['lureo-glass']) },
  'lureo-radius': { deps: [], make: (ctx, fresh) => (fresh ? `${pick(ctx, [0, 4, 6, 8, 10, 12, 14, 16]) / 16}rem` : ctx.tokens['lureo-radius']) },
  'lureo-border-width': { deps: [], make: (ctx, fresh) => (fresh ? pick(ctx, ['1px', '1px', '1px', '1.5px']) : ctx.tokens['lureo-border-width']) },
  'lureo-font-ui': { deps: [], make: (ctx, fresh) => (fresh ? `'${pick(ctx, UI_FACES)}', ui-sans-serif, system-ui, sans-serif` : ctx.tokens['lureo-font-ui']) },
  'lureo-font-prose': { deps: ['lureo-font-ui'], make: ctx => ctx.tokens['lureo-font-ui'] },
  // Titles in the interface face, at Lureo's weights; labels as written; no OpenType features
  'lureo-font-display': { deps: ['lureo-font-ui'], make: ctx => ctx.tokens['lureo-font-ui'] },
  'lureo-weight-text': { deps: [], make: (ctx, fresh) => (fresh ? pick(ctx, ['400', '400', '400', '300']) : ctx.tokens['lureo-weight-text']) },
  'lureo-weight-heading': { deps: [], make: (ctx, fresh) => (fresh ? pick(ctx, ['600', '700', '700', '800']) : ctx.tokens['lureo-weight-heading']) },
  'lureo-label-case': { deps: [], make: (ctx, fresh) => (fresh ? (ctx.random() < 0.2 ? 'uppercase' : 'none') : ctx.tokens['lureo-label-case']) },
  'lureo-label-tracking': {
    deps: ['lureo-label-case'],
    make: (ctx, fresh) => (ctx.tokens['lureo-label-case'] !== 'uppercase' ? '0em' : fresh || ctx.tokens['lureo-label-tracking'] === '0em' ? pick(ctx, ['0.04em', '0.06em']) : ctx.tokens['lureo-label-tracking']),
  },
  'lureo-font-features': { deps: [], make: (ctx, fresh) => (fresh ? 'normal' : ctx.tokens['lureo-font-features']) },
  // Buttons as round as the large corners or full pills; tags round when the corners are square
  'lureo-radius-control': {
    deps: ['lureo-radius'],
    make: (ctx, fresh) => (fresh ? pick(ctx, [largeCorner(ctx), largeCorner(ctx), '9999px']) : ctx.tokens['lureo-radius-control'] === '9999px' ? '9999px' : largeCorner(ctx)),
  },
  // A generated set's pill buttons take its button corners, as a brand's do
  'lureo-radius-button-pill': { deps: ['lureo-radius-control'], make: ctx => ctx.tokens['lureo-radius-control'] },
  // The hero band from the emphasis into the secondary, as Lureo's own
  'lureo-gradient-from': { deps: ['lureo-emphasis'], make: ctx => ctx.tokens['lureo-emphasis'] },
  'lureo-gradient-via': { deps: ['lureo-secondary'], make: ctx => ctx.tokens['lureo-secondary'] },
  'lureo-gradient-to': { deps: ['lureo-secondary'], make: ctx => ctx.tokens['lureo-secondary'] },
  'lureo-gradient-angle': { deps: [], make: (ctx, fresh) => (fresh ? pick(ctx, ['to bottom right', 'to bottom right', 'to right', '160deg']) : ctx.tokens['lureo-gradient-angle']) },
  'lureo-font-mono': { deps: [], make: (ctx, fresh) => (fresh ? `'${pick(ctx, MONO_FACES)}', ui-monospace, SFMono-Regular, Menlo, monospace` : ctx.tokens['lureo-font-mono']) },
  'lureo-texture-pattern': { deps: [], make: (ctx, fresh) => (fresh ? (ctx.random() < 0.6 ? 'none' : pick(ctx, PATTERN_NAMES)) : ctx.tokens['lureo-texture-pattern']) },
  'lureo-texture-size': { deps: [], make: (ctx, fresh) => (fresh ? pick(ctx, ['128px', '256px', '384px', '512px']) : (ctx.tokens['lureo-texture-size'] ?? '256px')) },
  'lureo-texture-strength': { deps: ['lureo-texture-pattern'], make: (ctx, fresh) => (ctx.tokens['lureo-texture-pattern'] === 'none' ? '0' : fresh || ctx.tokens['lureo-texture-strength'] === '0' ? between(ctx, 0.02, 0.06).toFixed(2) : ctx.tokens['lureo-texture-strength']) },
  'lureo-logo-style': { deps: [], make: (ctx, fresh) => (fresh ? (ctx.random() < 0.5 ? 'artwork' : 'shape') : ctx.tokens['lureo-logo-style']) },
};

// The palette: each hue a bright fill, readable with deep text on it
const PALETTE_HUES: Record<string, number> = { 'lureo-aqua': 185, 'lureo-mint': 165, 'lureo-coral': 40, 'lureo-sky': 240, 'lureo-lavender': 295 };
for (const [name, hue] of Object.entries(PALETTE_HUES)) {
  RULES[name] = {
    deps: ['lureo-emphasis', 'lureo-emphasis-strong'],
    make: (ctx, fresh) => {
      const colour = fresh ? { l: between(ctx, 0.8, 0.9), c: between(ctx, 0.08, 0.13), h: wrap(hue + between(ctx, -8, 8)) } : colourOf(ctx, name);
      // Deep text on it reads: lighten until both deep fills reach the bar
      let l = colour.l;
      while (l < 0.98 && ['lureo-emphasis', 'lureo-emphasis-strong'].some(text => contrast(oklchToSrgb({ ...colour, l }), rgbOf(ctx, text)) < 4.6)) l += 0.01;
      return formatOklch({ ...colour, l });
    },
  };
}
RULES['lureo-teal'] = { deps: ['lureo-surface'], make: inked('lureo-page', 4.6, ctx => wrap(205 + between(ctx, -8, 8)), () => 0.11, 'lureo-teal') };
RULES['lureo-violet'] = { deps: ['lureo-on-emphasis'], make: filled('lureo-violet', 'lureo-on-emphasis', 3, ctx => ({ l: between(ctx, 0.46, 0.55), c: between(ctx, 0.1, 0.15), h: wrap(290 + between(ctx, -8, 8)) })) };
RULES['lureo-grey-strong'] = { deps: ['lureo-surface'], make: ctx => fromPage(ctx, -0.36, 0.32, 0.5) };

// Each palette ink: its fill's hue and chroma, as dark as it must be to read as text on the page (as light, on a dark one)
for (const fill of FILL_SLOTS) {
  RULES[`${fill}-ink`] = {
    deps: ['lureo-surface', 'lureo-ink-strong', fill],
    make: ctx => {
      const colour = colourOf(ctx, fill);
      const bar = Math.min(4.5, 0.9 * contrast(rgbOf(ctx, 'lureo-ink-strong'), rgbOf(ctx, 'lureo-page'))) + 0.01;
      return readable(ctx, colour, 'lureo-page', bar);
    },
  };
}

// A token with no formula and no rule of its own keeps its value through every randomise
for (const { name } of BASE_TOKENS) RULES[name] ??= { deps: [], make: ctx => ctx.tokens[name] };

// The order rules run in: every token with no formula, its dependencies first
const ORDER = (() => {
  const names = BASE_TOKENS.map(token => token.name);
  const done: string[] = [];
  const visit = (name: string, path: string[] = []) => {
    if (done.includes(name)) return;
    if (path.includes(name)) throw new Error(`Generator rules loop: ${[...path, name].join(' → ')}`);
    for (const dep of RULES[name].deps) visit(dep, [...path, name]);
    done.push(name);
  };
  names.forEach(name => visit(name));
  return done;
})();

/** Fresh decisions for a theme. */
export function randomSeeds(random: Random, mode?: 'light' | 'dark'): ThemeSeeds {
  return {
    mode: mode ?? (random() < 0.5 ? 'light' : 'dark'),
    hue: random() * 360,
    tint: 0.005 + random() * 0.03,
    scheme: SCHEMES[Math.floor(random() * SCHEMES.length)],
    vivid: 0.1 + random() * 0.08,
  };
}

/** The decisions a theme already embodies: its page's hue and tint, its brand's vividness. */
export function seedsOf(tokens: Record<string, string>, mode: 'light' | 'dark'): ThemeSeeds {
  const ctx = { seeds: randomSeeds(Math.random, mode), random: Math.random, tokens };
  const page = colourOf(ctx, 'lureo-page');
  const brand = colourOf(ctx, 'lureo-brand');
  return { mode, hue: page.c > 0.004 ? page.h : brand.h, tint: Math.max(page.c, 0.004), scheme: SCHEMES[0], vivid: Math.max(brand.c, 0.08) };
}

const pinned = (name: string, pins: Pins) => pins.tokens.has(name) || pins.groups.has(groupOf(name));
const inScope = (name: string, scope: Scope) => scope.level === 'theme' || (scope.level === 'group' ? groupOf(name) === scope.id : scope.name === name);

/**
 * A theme's tokens randomised in `scope`, a plain value for every token: tokens in scope and not pinned are drawn fresh, or
 * made by their formula; any other token not pinned that reads one that changed is solved or made again; the rest keep
 * their values. Tries a few draws for one that reads everywhere, and returns the best with whatever it still falls short
 * on (pinned tokens can hold it back).
 */
export function randomise(
  tokens: Record<string, string>,
  mode: 'light' | 'dark',
  scope: Scope,
  pins: Pins = NO_PINS,
  random: Random = Math.random,
): { tokens: Record<string, string>; shortfalls: ContrastShortfall[]; seeds: ThemeSeeds } {
  let best: { tokens: Record<string, string>; shortfalls: ContrastShortfall[]; seeds: ThemeSeeds } | null = null;
  for (let attempt = 0; attempt < 12; attempt++) {
    const seeds = scope.level === 'theme' ? randomSeeds(random, mode) : { ...seedsOf(tokens, mode), scheme: SCHEMES[Math.floor(random() * SCHEMES.length)] };
    // While drawing, the tokens with a formula, no pin and in scope are their formulas, so a draw reads them as they will be
    // made; the rest read as the set holds them, as they keep those values unless a token they read changes
    const live = FORMULA_TOKENS.filter(name => !pinned(name, pins));
    const drawnAsFormula = live.filter(name => inScope(name, scope));
    const ctx: Context = { seeds, random, tokens: { ...tokens, ...Object.fromEntries(drawnAsFormula.map(name => [name, deriveCss(tokenSpec(name)!.derive!)])) } };
    const changed = new Set<string>();
    for (const name of ORDER) {
      if (pinned(name, pins)) continue;
      const fresh = inScope(name, scope);
      if (!fresh && !RULES[name].deps.some(dep => changed.has(dep))) continue;
      const next = RULES[name].make(ctx, fresh);
      if (next !== ctx.tokens[name]) {
        ctx.tokens[name] = next;
        changed.add(name);
      }
    }
    // Then made as plain values: the ones in scope and the ones that read a token that changed; the rest keep theirs
    const drawn = Object.fromEntries(BASE_TOKENS.map(token => [token.name, ctx.tokens[token.name]]));
    const readers = readersOf(changed);
    const remade = live.filter(name => inScope(name, scope) || readers.has(name));
    const made = madeByFormula({ ...tokens, ...drawn }, mode, remade);
    const shortfalls = contrastShortfalls(made, mode);
    if (!best || shortfalls.length < best.shortfalls.length) best = { tokens: made, shortfalls, seeds };
    if (shortfalls.length === 0) break;
  }
  return best!;
}

const HUE_NAMES: [number, string][] = [
  [15, 'Rose'], [40, 'Red'], [65, 'Orange'], [95, 'Amber'], [120, 'Lime'], [155, 'Green'], [180, 'Jade'], [200, 'Teal'],
  [225, 'Cyan'], [255, 'Blue'], [285, 'Indigo'], [315, 'Violet'], [345, 'Magenta'], [360, 'Rose'],
];

/** A literal name for a generated theme, its brand's hue and its scheme ("Teal triadic"), numbered when taken. */
export function generatedName(tokens: Record<string, string>, seeds: ThemeSeeds, taken: Iterable<string>): string {
  const brand = readColour(tokens['lureo-brand'] ?? '');
  const hue = brand ? srgbToOklch(brand.rgb).h : seeds.hue;
  const base = `${HUE_NAMES.find(([upTo]) => hue < upTo)?.[1] ?? 'Rose'} ${seeds.scheme.label}`;
  const used = new Set([...taken].map(name => name.trim().toLowerCase()));
  if (!used.has(base.toLowerCase())) return base;
  for (let n = 2; ; n++) if (!used.has(`${base} ${n}`.toLowerCase())) return `${base} ${n}`;
}
